import { type QueryClient, useQueryClient } from "@tanstack/react-query"
import { useRouter } from "@tanstack/react-router"
import { clearChatCache } from "@/model/live/chat-cache"
import { closeAllProjects } from "@/model/live/connection"
import type { createAppRouter } from "@/router"
import { authClient } from "./auth-client"
import { dropEarlyWorkspaces, sessionQuery, workspacesQuery } from "./queries"

type AppRouter = ReturnType<typeof createAppRouter>

/** Forget everything tied to the signed-in user: cached API data, open project sockets and their stores, cached chat transcripts. */
export const clearUserState = (queryClient: QueryClient) => {
  queryClient.clear()
  dropEarlyWorkspaces()
  closeAllProjects()
  clearChatCache()
}

/**
 * Leave the app for /sign-in. The session is marked signed-out first so the guest guard lets the
 * sign-in page render; the rest of the cache is dropped only after the old page has unmounted, so its
 * queries don't refetch (and 401) in between.
 */
const leaveForSignIn = async (queryClient: QueryClient, router: AppRouter, redirect?: string) => {
  queryClient.setQueryData(sessionQuery.queryKey, null)
  await router.navigate({ to: "/sign-in", search: redirect ? { redirect } : {} })
  clearUserState(queryClient)
}

/**
 * Called for every query or mutation that fails with 401: the server session ended (expired, revoked,
 * cookie gone) while a page was open. Send the person to sign-in, then back to where they were.
 */
export const handleUnauthorized = (queryClient: QueryClient, router: AppRouter) => {
  // Already known signed-out: a sign-out or an earlier 401 is handling it.
  if (queryClient.getQueryData(sessionQuery.queryKey) === null) return
  const { pathname, href } = router.state.location
  if (pathname === "/sign-in" || pathname === "/sign-up") return
  void leaveForSignIn(queryClient, router, href === "/" ? undefined : href)
}

/**
 * Called for every 403 (earlier settings spec §1.4). `not_member`: re-read the workspace list, then re-run the route
 * guards, so the workspace route shows "no access" instead of a broken page. `forbidden`: the role may have changed;
 * re-read the list (it carries the role), and the calling component shows the message.
 */
export const handleForbidden = (queryClient: QueryClient, router: AppRouter, notMember: boolean) => {
  void queryClient.invalidateQueries({ queryKey: workspacesQuery.queryKey }).then(() => {
    if (notMember) void router.invalidate()
  })
}

export function useSignOut() {
  const queryClient = useQueryClient()
  const router = useRouter()
  return async () => {
    await authClient.signOut()
    await leaveForSignIn(queryClient, router)
  }
}
